import { useMemo, useState } from "react";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import clsx from "clsx";
import { userQueries } from "@/entities/user";
import { StatusDeclinedIcon, VectorIcon } from "@/shared/assets/svg/icons";
import { fallbackString } from "@/shared/utils";
import { Can, Subjects } from "@shared/lib/casl";
import { UserBubble } from "@shared/ui";
import "./rejected-records-collapsible.scss";

type SectionOneItem = {
  sequenceNumber?: number;
  status?: string;
  comment?: string;
  comments?: Array<{ 
    comment?: string; 
    commentText?: string;
    type?: string; 
    updateUserId?: string; 
    createDate?: string;
    [key: string]: any;
  }>;
  updateUserId?: string;
  [key: string]: any;
};

export const RejectedRecordsCollapsible = ({
  items,
  onReturnToWork,
  editSubject,
}: {
  items: SectionOneItem[];
  onReturnToWork: (item: SectionOneItem) => void;
  editSubject: Subjects;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  const safeItems = items ?? [];
  const current = safeItems[idx];

  let currentUpdateUserId = current?.updateUserId ?? "";
  
  if (!currentUpdateUserId && current?.comments && Array.isArray(current.comments) && current.comments.length > 0) {
    const isSectionFour = current.itemPartOne !== undefined || current.itemPartTwo !== undefined;
    
    if (isSectionFour) {
      const isPartOneRejected = current?.itemPartOne?.status === "REJECTED";
      const isPartTwoRejected = current?.itemPartTwo?.status === "REJECTED";
      const itemPartOneId = current?.itemPartOne?.id;
      const itemPartTwoId = current?.itemPartTwo?.id;
      
      const rejectedPartComment = current.comments.find((c: any) => {
        const commentItemPart = c?.itemPart;
        return (
          (isPartOneRejected && commentItemPart === itemPartOneId) ||
          (isPartTwoRejected && commentItemPart === itemPartTwoId)
        );
      });
      
      currentUpdateUserId = rejectedPartComment?.updateUserId ?? "";
    } else {
      currentUpdateUserId = current.comments[0]?.updateUserId ?? "";
    }
  }
  const { data: userContact } = useQuery({
    ...userQueries.userContact(currentUpdateUserId),
    enabled: Boolean(currentUpdateUserId),
  });

  const userLabel = useMemo(() => {
    if (!userContact) return "Пользователь";
    const surname = (userContact.userSurname ?? "").toString().trim();
    const first = (userContact.userFirstName ?? "").toString().trim();
    const middle = (userContact.userMiddleName ?? "").toString().trim();
    const f = first ? `${first[0]}.` : "";
    const m = middle ? `${middle[0]}.` : "";
    return fallbackString(`${surname} ${f}${m}`.trim());
  }, [userContact]);

  const canPrev = idx > 0;
  const canNext = idx < safeItems.length - 1;

  if (!current) return null;

  return (
    <div className={clsx("rejected-records", isOpen && "rejected-records--open")}>
      <button
        type="button"
        className="rejected-records__header"
        onClick={() => setIsOpen((v) => !v)}
      >
        <div className="rejected-records__header-left">
          <span className="rejected-records__header-icon">
            <StatusDeclinedIcon />
          </span>
          <span className="rejected-records__header-title">Отклоненные записи</span>
        </div>
        <span
          className={clsx("rejected-records__toggle", isOpen && "rejected-records__toggle--open")}
        >
          <VectorIcon />
        </span>
      </button>

      <div className={clsx("rejected-records__collapsible", isOpen && "is-open")}>
        <div className="rejected-records__collapsible-inner">
          <div className="rejected-records__subtitle">
            Верните отклоненную запись в работу, чтобы внести изменения исходя из
            комментария
          </div>

          <div className="rejected-records__card">
            <div className="rejected-records__card-header">
              <div className="rejected-records__card-title">
                Запись №{current.sequenceNumber}
              </div>
              <Can I="edit" a={editSubject}>
                <Button type="primary" onClick={() => onReturnToWork(current)}>
                  Вернуть в работу
                </Button>
              </Can>
            </div>

            <div className="rejected-records__comment-meta">
              Комментарий от{" "}
              <UserBubble userId={currentUpdateUserId}>
                <Button type="link" className="rejected-records__comment-author">
                  {userLabel}
                </Button>
              </UserBubble>
            </div>

            <div className="rejected-records__comment-box">
              {(() => {
                if (current.comments && Array.isArray(current.comments) && current.comments.length > 0) {
                  const isSectionFour = current.itemPartOne !== undefined || current.itemPartTwo !== undefined;
                  let commentsToShow = current.comments;
                  
                  if (isSectionFour) {
                    const isPartOneRejected = current?.itemPartOne?.status === "REJECTED";
                    const isPartTwoRejected = current?.itemPartTwo?.status === "REJECTED";
                    const itemPartOneId = current?.itemPartOne?.id;
                    const itemPartTwoId = current?.itemPartTwo?.id;
                    
                    commentsToShow = current.comments.filter((c: any) => {
                      const commentItemPart = c?.itemPart;
                      return (
                        (isPartOneRejected && commentItemPart === itemPartOneId) ||
                        (isPartTwoRejected && commentItemPart === itemPartTwoId)
                      );
                    });
                  }
                  
                  const validComments = commentsToShow.filter(
                    (c: any) => {
                      const commentText = c?.commentText ?? c?.comment;
                      return commentText && commentText.toString().trim();
                    },
                  );
                  if (validComments.length > 0) {
                    return validComments
                      .map((c: any) => c?.commentText ?? c?.comment)
                      .join("\n");
                  }
                }
                return fallbackString(current.comment);
              })()}
            </div>
          </div>

          <div className="rejected-records__footer">
            <div className="rejected-records__remaining">
              Осталось вернуть в работу - {safeItems.length}
            </div>

            <div className="rejected-records__pager">
              <button
                type="button"
                className="rejected-records__pager-btn"
                disabled={!canPrev}
                onClick={() => canPrev && setIdx((v) => v - 1)}
                aria-label="Предыдущая запись"
              >
                <LeftOutlined />
              </button>
              <button
                type="button"
                className="rejected-records__pager-btn rejected-records__pager-btn--primary"
                disabled={!canNext}
                onClick={() => canNext && setIdx((v) => v + 1)}
                aria-label="Следующая запись"
              >
                <RightOutlined />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


